/**
 * Coordinate frame and units shared by every pass.
 *
 * Model coordinates are Å in the file's own (domain) frame. Nanome converts domain -> Unity by swapping y and z
 * (Nanome.Adapter/Math/Conversion.cs:8-11, "ReverseChirality"), and Unity is left-handed with the camera looking
 * down +z. Our GL frame is right-handed with the camera looking down -z, so a Unity vector (x, y, z) is the GL vector
 * (x, y, -z). Composing both: domain (x, y, z) -> GL (x, z, -y). That map is a proper rotation (90 deg about x,
 * det = +1), so it is folded into the scene root matrix instead of rewriting every buffer.
 *
 * 1 Å = 0.02 m (RenderingViewModel.cs:44; SceneManager.prefab:36) is the scene root's initial uniform scale.
 */
import { Matrix4, Quaternion, Vector3 } from 'three';
import { ANGSTROM_TO_METRE } from '@nanome-lite/look';

/** Domain -> GL chirality rotation as a 4x4 (column-major, three.js convention). */
export const CHIRALITY = new Matrix4().set(
  1, 0, 0, 0,
  0, 0, 1, 0,
  0, -1, 0, 0,
  0, 0, 0, 1,
);

/** domain (x, y, z) Å -> GL frame (x, z, -y), still in Å (no scale). */
export function domainToGl(x: number, y: number, z: number, out = new Vector3()): Vector3 {
  return out.set(x, z, -y);
}

/** Scene root transform (position in metres, rotation, uniform scale metres per Å). The trackball moves this, not the camera. */
export interface SceneTransform {
  position: Vector3;
  quaternion: Quaternion;
  /** metres per Å; initial value ANGSTROM_TO_METRE */
  scale: number;
}

export function createSceneTransform(): SceneTransform {
  return { position: new Vector3(0, 0, 0), quaternion: new Quaternion(), scale: ANGSTROM_TO_METRE };
}

export function copySceneTransform(dst: SceneTransform, src: SceneTransform): SceneTransform {
  dst.position.copy(src.position);
  dst.quaternion.copy(src.quaternion);
  dst.scale = src.scale;
  return dst;
}

export function cloneSceneTransform(src: SceneTransform): SceneTransform {
  return copySceneTransform(createSceneTransform(), src);
}

const _s = new Vector3();
/** Root model matrix: T(position) R(quaternion) S(scale) * CHIRALITY. Maps domain Å to GL metres (camera at origin). */
export function sceneRootMatrix(t: SceneTransform, out = new Matrix4()): Matrix4 {
  out.compose(t.position, t.quaternion, _s.set(t.scale, t.scale, t.scale));
  return out.multiply(CHIRALITY);
}

/** Transform a domain Å point to GL world metres with the current root transform. */
export function domainToWorld(t: SceneTransform, x: number, y: number, z: number, out = new Vector3()): Vector3 {
  domainToGl(x, y, z, out);
  out.multiplyScalar(t.scale).applyQuaternion(t.quaternion).add(t.position);
  return out;
}
